/* eslint-disable class-methods-use-this */
if (!customElements.get("mega-menu")) {
  class MegaMenu extends HTMLElement {
    constructor() {
      super();
      this.mouseEnterHandlers = new Map();
      this.mouseLeaveHandlers = new Map();
      this.megaMenuHoverHandler = null;
    }

    static isFrenchWithCHF() {
      // Check for French locale and CHF currency
      const locale = window.Shopify?.locale;
      const currency = window.Shopify?.currency?.active;
      return locale?.startsWith("fr") && currency === "CHF";
    }

    static getMenuData() {
      // Check if French locale and CHF currency
      const isFrenchWithCHF = MegaMenu.isFrenchWithCHF();

      // Get appropriate menu data
      if (isFrenchWithCHF && window.menu_json_fr) {
        return window.menu_json_fr;
      }

      return window.menu_json;
    }

    connectedCallback() {
      try {
        let hasContentInjected = false; // Flag to track content injection

        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1]; // Get the latest LCP entry
          if (lastEntry && !hasContentInjected) {
            // Check if not already injected
            this.injectContent();
            this.handleMegaMenuHeight();
            this.handleMegaMenuHover();
            hasContentInjected = true; // Set flag indicating content has been injected
            observer.disconnect(); // Disconnect observer after LCP is detected
          }
        });

        observer.observe({ type: "largest-contentful-paint", buffered: true });

        // Fallback for browsers where PerformanceObserver might not trigger
        setTimeout(() => {
          if (!observer.takeRecords().length && !hasContentInjected) {
            // Additional check for flag
            this.injectContent();
            this.handleMegaMenuHeight();
            this.handleMegaMenuHover();
            hasContentInjected = true; // Set the flag after fallback execution
          }
        }, 2000); // Adjust the timeout duration as needed
      } catch (error) {
        // Fallback in case PerformanceObserver fails
        if (!hasContentInjected) {
          // Check if not already injected
          this.injectContent();
          this.handleMegaMenuHeight();
          this.handleMegaMenuHover();
          hasContentInjected = true; // Set flag after fallback execution
        }
      }
    }

    disconnectedCallback() {
      // Cleanup event listeners and timeouts when the element is removed from the DOM
      this.cleanupMegaMenuHover();
    }

    trackNavEvent(level, link, linkText) {
      link.addEventListener("click", () => {
        window.dataLayer.push({
          event: "select_navitem_dev",
          nav_level: level,
          nav_type: "main-navigation",
          category_name: linkText,
          category_id: linkText,
        });
      });
    }

    injectContent() {
      const menuData = MegaMenu.getMenuData();
      if (!menuData || !menuData.menuItems) return;

      const menuItemsContainer = this.querySelector(".mega-menu-items");
      const submenusContainer = this.querySelector(".mega-menu-submenus-container");

      menuData.menuItems.forEach((menuItem, index) => {
        const menuItemElement = document.createElement("li");
        menuItemElement.className = `mega-menu-item ${index === 0 ? "mega-menu-item--active" : ""}`;
        menuItemElement.setAttribute("menu-data", `menu-${index + 1}`);
        menuItemElement.setAttribute("role", "menuitem");

        menuItemElement.innerHTML = `
            <div class="image mega-menu-item__img" style="--object-fit: contain">
              <img src="${menuItem.image}" class="image__img image--contain" loading="lazy" alt="${menuItem.title}" />
            </div>
            <a href="${menuItem.url}" class="text text--x-sm text--regular text--line-height-sm text--body-font text--none-decoration" style="--text-color: var(--color--secondary);">
              ${menuItem.title}
            </a>
            <div class="mega-menu-item__svg">
              <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                <path d="M1 1.65945L6.08884 6.74829C6.12191 6.78131 6.14814 6.82053 6.16604 6.8637C6.18394 6.90687 6.19316 6.95315 6.19316 6.99988C6.19316 7.04662 6.18394 7.09289 6.16604 7.13606C6.14814 7.17923 6.12191 7.21845 6.08884 7.25147L1 12.3403" stroke="#002341" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          `;

        menuItemsContainer.appendChild(menuItemElement);

        // Creating submenu
        if (menuItem.subMenu && menuItem.subMenu.length > 0) {
          const submenuElement = document.createElement("div");
          submenuElement.className = `mega-menu-submenus ${index === 0 ? "mega-menu-submenus--active" : ""}`;
          submenuElement.setAttribute("menu-data", `menu-${index + 1}`);
          submenuElement.setAttribute("data-index", index);

          const submenuWrapper = document.createElement("div");
          submenuWrapper.className = "mega-menu-submenus__wrapper";

          menuItem.subMenu.forEach((subItem) => {
            const subCategory = document.createElement("div");
            subCategory.className = "mega-menu-submenu";

            subCategory.innerHTML = `
                <div class="image mega-menu-submenu__img" style="--object-fit: contain">
                  ${subItem.image ? `<img src="${subItem.image}" class="image__img image--contain" loading="lazy" alt="${subItem.title}" />` : ""}
                </div>
                <ul class="mega-menu-submenu__container">
                  <a href="${subItem.url}" class="mega-menu-submenu__title text text--x-sm text--bold text--line-height-sm text--body-font text--none-decoration mega-menu__item-breaker" style="--text-color: var(--color--secondary);">
                    ${subItem.title}
                  </a>
                </ul>
              `;

            // Third level items
            subItem.subMenu?.forEach((thirdLevelItem, thirdIndex) => {
              if (thirdIndex === 5) {
                subCategory.querySelector(".mega-menu-submenu__container").innerHTML += `
                    <li class="mega-menu-submenu__item">
                      <a href="${subItem.url}" class="text text--xx-sm text--regular text--line-height-sm text--body-font text--underline mega-menu-submenu__more" style="--text-color: var(--color--secondary);">
                        ${this.getAttribute('view-all') || 'Tout afficher'}
                      </a>
                    </li>
                  `;
                return;
              }
              if (thirdIndex > 5) return;
              subCategory.querySelector(".mega-menu-submenu__container").innerHTML += `
                  <li class="mega-menu-submenu__item">
                    <a href="${thirdLevelItem.url}" class="text text--xx-sm text--regular text--line-height-sm text--body-font text--none-decoration mega-menu__item-breaker" style="--text-color: var(--color--secondary);">
                      ${thirdLevelItem.title}
                    </a>
                  </li>
                `;
            });

            submenuWrapper.appendChild(subCategory);
          });

          submenuElement.appendChild(submenuWrapper);
          submenusContainer.appendChild(submenuElement);
        }
      });
      // Level 2

      document.querySelectorAll(".mega-menu-item").forEach((item) => {
        const link = item.querySelector("a");
        if (link) {
          this.trackNavEvent("2", item, link.textContent?.trim());
        }
      });

      // Level 3
      document.querySelectorAll(".mega-menu-submenu__title, .mega-menu-submenu__item a").forEach((link) => {
        this.trackNavEvent("3", link, link.textContent?.trim());
      });
    }

    // Get mega menu height
    handleMegaMenuHeight() {
      const setMegaMenuHeight = () => {
        const headerGroups = document.querySelectorAll(".shopify-section-group-header-group");
        if (headerGroups.length === 0) {
          // Retry if elements are not available yet
          setTimeout(setMegaMenuHeight, 100); // Retry every 100ms
          return;
        }

        // Calculate the total height of all header groups
        let height = 0;
        headerGroups.forEach((hg) => {
          height += hg.getBoundingClientRect().height; // Using getBoundingClientRect to account for borders/padding
        });

        // Set the CSS variable for the mega menu height
        this.style.setProperty("--mega-menu-height", `calc(100vh - ${height}px)`);
      };

      // Call the height function immediately if the DOM is already loaded
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setMegaMenuHeight);
      } else {
        setMegaMenuHeight();
      }

      // Listen for window resize events to adjust the height dynamically
      window.addEventListener("resize", setMegaMenuHeight);

      // Optional: If dynamic changes happen, use MutationObserver to monitor the DOM
      const observer = new MutationObserver(setMegaMenuHeight);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // Handle mega menu hover with MutationObserver
    handleMegaMenuHover() {
      const megaMenuItems = this.querySelectorAll(".mega-menu__item");

      // MutationObserver to detect added menu items
      const menuObserver = new MutationObserver(() => {
        const updatedMenuItems = this.querySelectorAll(".mega-menu__item");
        updatedMenuItems.forEach((item) => {
          if (!item.hasAttribute("data-listener-attached")) {
            this.attachHoverListeners(item);
            item.setAttribute("data-listener-attached", "true");
          }
        });
      });

      // Start observing for changes in the mega menu container
      menuObserver.observe(this, { childList: true, subtree: true });

      // Initially attach listeners for already existing items
      megaMenuItems.forEach((item) => {
        if (!item.hasAttribute("data-listener-attached")) {
          this.attachHoverListeners(item);
          item.setAttribute("data-listener-attached", "true");
        }
      });
    }

    // Attach hover listeners to a single mega menu item
    attachHoverListeners(item) {
      const megaMenu = item.querySelector(".mega-menu-component");
      if (megaMenu) {
        let leaveTimeout;
        let enterTimeout;

        // Create the mouseEnter and mouseLeave handlers
        const mouseEnterHandler = () => {
          clearTimeout(leaveTimeout); // Clear any pending leave timeout
          enterTimeout = setTimeout(() => {
            if (item.matches(":hover")) {
              megaMenu.classList.add("mega-menu-component--active");
              document.querySelector("body").classList.add("overflow-hidden");
            }
          }, 200);
        };

        const mouseLeaveHandler = () => {
          clearTimeout(enterTimeout); // Clear any pending enter timeout
          leaveTimeout = setTimeout(() => {
            megaMenu.classList.remove("mega-menu-component--active");
            document.querySelector("body").classList.remove("overflow-hidden");
          }, 200);
        };

        // Store the handlers for later removal
        this.mouseEnterHandlers.set(item, mouseEnterHandler);
        this.mouseLeaveHandlers.set(item, mouseLeaveHandler);

        // Attach the event listeners
        item.addEventListener("mouseenter", mouseEnterHandler);
        item.addEventListener("mouseleave", mouseLeaveHandler);

        this.handleMegaMenuItemHover(megaMenu);
      }
    }

    // Handle mega menu item hover
    handleMegaMenuItemHover(megaMenu) {
      const megaMenuItems = megaMenu.querySelectorAll(".mega-menu-item");
      const submenus = megaMenu.querySelectorAll(".mega-menu-submenus");

      megaMenuItems.forEach((megaMenuItem, itemIndex) => {
        megaMenuItem.addEventListener("mouseenter", (e) => {
          if (this.megaMenuHoverHandler) {
            clearTimeout(this.megaMenuHoverHandler);
          }

          this.megaMenuHoverHandler = setTimeout(() => {
            if (e.target.matches(":hover")) {
              // Clear any pending leave timeout
              megaMenuItems.forEach((item) => item.classList.remove("mega-menu-item--active"));
              megaMenuItem.classList.add("mega-menu-item--active");

              // Render the current submenu from the menu data
              submenus.forEach((submenu) => submenu.classList.remove("mega-menu-submenus--active"));

              const menuData = MegaMenu.getMenuData();
              const currentSubmenuData = menuData.menuItems[itemIndex];
              if (currentSubmenuData && currentSubmenuData.subMenu) {
                megaMenu
                  .querySelector(`.mega-menu-submenus[data-index="${itemIndex}"]`)
                  .classList.add("mega-menu-submenus--active");
              }
            }
          }, 50);
        });
      });
    }

    // Cleanup hover event listeners
    cleanupMegaMenuHover() {
      const megaMenuItems = this.querySelectorAll(".mega-menu__item");
      megaMenuItems.forEach((item) => {
        const megaMenu = item.querySelector(".mega-menu-component");
        if (megaMenu) {
          // Remove the event listeners using the stored handlers
          item.removeEventListener("mouseenter", this.mouseEnterHandlers.get(item));
          item.removeEventListener("mouseleave", this.mouseLeaveHandlers.get(item));
        }
      });

      // Clear the handlers after they are removed
      this.mouseEnterHandlers.clear();
      this.mouseLeaveHandlers.clear();
    }
  }

  customElements.define("mega-menu", MegaMenu);
}